import { FC, useEffect, useRef, useState } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import { AddconMappingPanel } from '@service/giftSeller/components/addcon/AddconMappingPanel';
import { ImageUploadBox } from '@service/seller/components/ImageUploadBox';
import {
  giftChoiceUnitDataAtomFamily,
  giftChoiceUnitListDataAtom,
  giftChoiceUnitStatusAtomFamily,
} from '@shared/api/giftChoiceUnit/giftChoiceUnit.atom';
import {
  createGiftChoiceUnit,
  updateGiftChoiceUnit,
} from '@shared/api/giftChoiceUnit/giftChoiceUnit.controller';
import { useGiftChoiceUnitHook } from '@shared/api/giftChoiceUnit/giftChoiceUnit.hook';
import {
  giftItemBrandListDataAtom,
  giftItemBrandListStatusAtom,
} from '@shared/api/giftItemBrand/giftItemBrand.atom';
import { useGiftItemBrandListHook } from '@shared/api/giftItemBrand/giftItemBrand.hook';
import { postImage } from '@shared/api/uploadImage';
import { Loading } from '@shared/components/Loading';
import { invalidateQueriesByUrlPart } from '@shared/hooks/recoil-query';
import { useAuth } from '@shared/state/hooks/useAuth';
import { useQueryClient } from 'react-query';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import styles from '../components/GiftBrandManagement.module.css';

interface FormData {
  giftItemBrand: string;
  name: string;
  description: string;
  priceRegular: number;
  priceWhole: number;
  wholeUrl: string;
  enabled: boolean;
  validPeriod: string;
  placeOfExchange: string;
}

export const GiftChoiceUnitFormPage: FC = function GiftChoiceUnitFormPage() {
  const { unitId } = useParams<{ unitId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { sellerRole, isAdmin } = useAuth();
  const queryClient = useQueryClient();

  const isEditMode = Boolean(unitId);

  // 수정 모드일 때 데이터 조회
  useGiftChoiceUnitHook(unitId ?? '', sellerRole(), !isEditMode);
  useGiftItemBrandListHook(sellerRole());

  const status = useRecoilValue(giftChoiceUnitStatusAtomFamily(unitId ?? ''));
  const unitData = useRecoilValue(giftChoiceUnitDataAtomFamily(unitId ?? ''));
  const brandListStatus = useRecoilValue(giftItemBrandListStatusAtom);
  const brandList = useRecoilValue(giftItemBrandListDataAtom);

  // 목록 데이터 업데이트용
  const setListData = useSetRecoilState(giftChoiceUnitListDataAtom);

  // 폼 상태
  const [formData, setFormData] = useState<FormData>({
    giftItemBrand: searchParams.get('brandId') || '',
    name: '',
    description: '',
    priceRegular: 0,
    priceWhole: 0,
    wholeUrl: '',
    enabled: true,
    validPeriod: '',
    placeOfExchange: '',
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 수정 모드일 때 기존 데이터 로드 — 최초 1회만. 창 포커스 refetch로 atom이
  // 새 객체가 되어도 입력 중인 값을 덮어쓰지 않는다.
  const didInitRef = useRef(false);
  useEffect(() => {
    if (isEditMode && unitData && !didInitRef.current) {
      didInitRef.current = true;
      setFormData({
        giftItemBrand:
          typeof unitData.giftItemBrand === 'object'
            ? unitData.giftItemBrand?._id || ''
            : unitData.giftItemBrand || '',
        name: unitData.name || '',
        description: unitData.description || '',
        priceRegular: unitData.priceRegular || 0,
        priceWhole: unitData.priceWhole || 0,
        wholeUrl: unitData.wholeUrl || '',
        enabled: unitData.enabled !== false,
        validPeriod: unitData.couponDetail?.validPeriod || '',
        placeOfExchange: unitData.couponDetail?.placeOfExchange || '',
      });
    }
  }, [isEditMode, unitData]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    if (['priceRegular', 'priceWhole'].includes(name)) {
      setFormData((prev) => ({ ...prev, [name]: Number(value) || 0 }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  // 제출 핸들러
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (
      isSubmitting ||
      (isEditMode && (status !== 'success' || !unitData || !didInitRef.current))
    )
      return;

    if (!formData.name.trim()) {
      alert('유닛명을 입력해주세요.');
      return;
    }

    try {
      setIsSubmitting(true);

      let imageId: string | undefined;

      // 이미지 업로드
      if (imageFile) {
        const uploadedImage = await postImage(imageFile, sellerRole());
        imageId = uploadedImage._id;
      }

      const payload = {
        giftItemBrand: formData.giftItemBrand || undefined,
        name: formData.name,
        description: formData.description || undefined,
        priceRegular: formData.priceRegular || undefined,
        priceWhole: formData.priceWhole || undefined,
        wholeUrl: formData.wholeUrl || undefined,
        enabled: formData.enabled,
        image: imageId,
        couponDetail:
          formData.validPeriod || formData.placeOfExchange
            ? {
                validPeriod: formData.validPeriod || undefined,
                placeOfExchange: formData.placeOfExchange || undefined,
              }
            : undefined,
      };

      if (isEditMode && unitId) {
        // 수정
        const updated = await updateGiftChoiceUnit(unitId, payload, sellerRole());
        setListData((prev) => prev.map((item) => (item._id === unitId ? updated : item)));
        alert('수정되었습니다.');
      } else {
        // 생성
        const created = await createGiftChoiceUnit(payload, sellerRole());
        setListData((prev) => [...prev, created]);
        alert('등록되었습니다.');
      }

      navigate('/gift-seller/choice-unit');
    } catch (error) {
      console.error(error);
      alert(isEditMode ? '수정에 실패했습니다.' : '등록에 실패했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // 로딩 중 (수정 모드)
  if (isEditMode && (status === 'loading' || status === 'idle')) {
    return (
      <div className="center-box min-h-screen">
        <Loading />
      </div>
    );
  }

  if (isEditMode && (status === 'error' || !unitData)) {
    return (
      <div className={`${styles.page} min-h-screen bg-gray-50 px-4 py-8 sm:px-6`}>
        <div
          role="alert"
          className="mx-auto max-w-3xl rounded-md border border-gray-200 bg-white p-8 text-center"
        >
          <h1 className="text-lg font-semibold">선택유닛 정보를 불러오지 못했습니다.</h1>
          <p className="mt-2 text-sm text-gray-500">다시 조회한 후 수정해 주세요.</p>
          <div className="mt-5 flex flex-wrap justify-center gap-2">
            <Link
              to="/gift-seller/choice-unit"
              className="rounded border border-gray-300 px-4 py-2 text-sm"
            >
              목록으로 돌아가기
            </Link>
            <button
              type="button"
              onClick={() => invalidateQueriesByUrlPart(queryClient, 'gift-choice-unit')}
              className="rounded bg-indigo-600 px-4 py-2 text-sm text-white"
            >
              다시 시도
            </button>
          </div>
        </div>
      </div>
    );
  }

  const fieldClass =
    'w-full min-w-0 rounded border border-gray-300 bg-white px-3 py-2 text-sm disabled:bg-gray-50';
  const labelClass = 'mb-1 block text-sm font-medium text-gray-700';
  const sectionClass = 'rounded-md border border-gray-200 bg-white p-5 sm:p-6';

  return (
    <div
      className={`${styles.page} min-h-screen bg-gray-50 px-4 py-6 text-gray-900 sm:px-6 lg:px-8 lg:py-8`}
    >
      <div className="mx-auto max-w-4xl">
        <header className="mb-6">
          <div className="flex items-center gap-3">
            <button
              type="button"
              aria-label="선택유닛 목록으로 돌아가기"
              onClick={() => navigate('/gift-seller/choice-unit')}
              disabled={isSubmitting}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded hover:bg-gray-100 disabled:opacity-40"
            >
              ←
            </button>
            <h1 className="text-2xl font-semibold tracking-tight">
              선택유닛 {isEditMode ? '수정' : '등록'}
            </h1>
          </div>
          <p className="mt-3 text-sm text-gray-500">
            쿠폰 상품에서 선택할 유닛의 정보와 가격을 설정합니다.
          </p>
        </header>
        <form onSubmit={handleSubmit}>
          <fieldset disabled={isSubmitting} className="min-w-0 space-y-4">
            <section className={sectionClass} aria-labelledby="unit-basic-title">
              <h2 id="unit-basic-title" className="text-base font-semibold">
                기본 정보
              </h2>
              <p className="mb-5 mt-1 text-sm text-gray-500">
                브랜드와 유닛 이름, 판매 상태를 관리합니다.
              </p>
              <div className="grid gap-5 sm:grid-cols-2">
                <div className="min-w-0">
                  <label htmlFor="unit-brand" className={labelClass}>
                    소속 브랜드
                  </label>
                  <select
                    id="unit-brand"
                    name="giftItemBrand"
                    value={formData.giftItemBrand}
                    onChange={handleChange}
                    className={fieldClass}
                    disabled={brandListStatus !== 'success'}
                  >
                    <option value="">브랜드 선택 (선택사항)</option>
                    {brandList.map((brand) => (
                      <option key={brand._id} value={brand._id}>
                        {brand.name}
                      </option>
                    ))}
                  </select>
                  {brandListStatus === 'error' && (
                    <div role="alert" className="mt-2 text-xs text-gray-600">
                      브랜드 목록을 불러오지 못했습니다.{' '}
                      <button
                        type="button"
                        onClick={() =>
                          invalidateQueriesByUrlPart(queryClient, 'gift-item-brand')
                        }
                        className="rounded py-1 text-indigo-700"
                      >
                        브랜드 다시 시도
                      </button>
                    </div>
                  )}
                </div>
                <div>
                  <span id="unit-enabled-label" className={labelClass}>
                    판매 상태
                  </span>
                  <div className="flex min-h-[38px] items-center gap-2">
                    <button
                      type="button"
                      role="switch"
                      aria-labelledby="unit-enabled-label"
                      aria-checked={formData.enabled}
                      onClick={() =>
                        setFormData((prev) => ({ ...prev, enabled: !prev.enabled }))
                      }
                      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                        formData.enabled ? 'bg-indigo-600' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                          formData.enabled ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                    <span className="text-sm text-gray-600">
                      {formData.enabled ? '판매중' : '판매중단'}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    판매중단 시 쿠폰 상품에서 해당 유닛이 제외됩니다.
                  </p>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="unit-name" className={labelClass}>
                    유닛명 <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="unit-name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="예: ICE 아메리카노, 8500원 음료"
                    required
                    className={fieldClass}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="unit-description" className={labelClass}>
                    설명
                  </label>
                  <textarea
                    id="unit-description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="선택유닛에 대한 설명 (선택사항)"
                    rows={3}
                    className={fieldClass}
                  />
                </div>
              </div>
            </section>
            <section className={sectionClass} aria-labelledby="unit-price-title">
              <h2 id="unit-price-title" className="text-base font-semibold">
                가격 및 도매 정보
              </h2>
              <p className="mb-5 mt-1 text-sm text-gray-500">
                정가와 내부 관리용 도매가를 입력합니다.
              </p>
              <div className="grid gap-5 sm:grid-cols-2">
                <div className="min-w-0">
                  <label htmlFor="unit-price-regular" className={labelClass}>
                    정가
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      id="unit-price-regular"
                      type="number"
                      name="priceRegular"
                      value={formData.priceRegular || ''}
                      onChange={handleChange}
                      placeholder="0"
                      className={fieldClass}
                    />
                    <span className="shrink-0 text-sm text-gray-500">원</span>
                  </div>
                </div>
                <div className="min-w-0">
                  <label htmlFor="unit-price-whole" className={labelClass}>
                    도매가
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      id="unit-price-whole"
                      type="number"
                      name="priceWhole"
                      value={formData.priceWhole || ''}
                      onChange={handleChange}
                      placeholder="0"
                      className={fieldClass}
                    />
                    <span className="shrink-0 text-sm text-gray-500">원</span>
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="unit-whole-url" className={labelClass}>
                    도매사이트 URL
                  </label>
                  <input
                    id="unit-whole-url"
                    type="url"
                    name="wholeUrl"
                    value={formData.wholeUrl}
                    onChange={handleChange}
                    placeholder="https://example.com/product/123"
                    className={fieldClass}
                  />
                </div>
              </div>
            </section>
            <section className={sectionClass} aria-labelledby="unit-coupon-title">
              <h2 id="unit-coupon-title" className="text-base font-semibold">
                쿠폰 상세 정보{' '}
                <span className="ml-1 text-xs font-normal text-gray-500">선택사항</span>
              </h2>
              <p className="mb-5 mt-1 text-sm text-gray-500">
                고객에게 안내할 유효기간과 교환처를 입력합니다.
              </p>
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="unit-valid-period" className={labelClass}>
                    유효기간
                  </label>
                  <input
                    id="unit-valid-period"
                    name="validPeriod"
                    value={formData.validPeriod}
                    onChange={handleChange}
                    placeholder="예: 발행일로부터 93일"
                    className={fieldClass}
                  />
                </div>
                <div>
                  <label htmlFor="unit-exchange" className={labelClass}>
                    교환처
                  </label>
                  <input
                    id="unit-exchange"
                    name="placeOfExchange"
                    value={formData.placeOfExchange}
                    onChange={handleChange}
                    placeholder="예: 전국 스타벅스 매장"
                    className={fieldClass}
                  />
                </div>
              </div>
            </section>
            {/* 발송 매체 · 애드콘 연결 — 폼 저장과 별개로 패널 안에서 바로 저장된다 */}
            {isEditMode && unitId && (
              <AddconMappingPanel
                target="choice-unit"
                id={unitId}
                deliveryChannel={unitData?.deliveryChannel}
                noReq={unitData?.addcon?.noReq}
                canEdit={isAdmin()}
                onSaved={({ deliveryChannel, noReq }) =>
                  setListData((prev) =>
                    prev.map((item) =>
                      item._id === unitId
                        ? {
                            ...item,
                            deliveryChannel: deliveryChannel ?? undefined,
                            addcon: noReq ? { noReq } : undefined,
                          }
                        : item,
                    ),
                  )
                }
              />
            )}
            <section className={sectionClass} aria-labelledby="unit-image-title">
              <h2 id="unit-image-title" className="text-base font-semibold">
                유닛 이미지
              </h2>
              <p className="mb-5 mt-1 text-sm text-gray-500">
                선택유닛을 대표하는 이미지를 업로드하세요.
              </p>
              <div className="max-w-[250px]">
                <ImageUploadBox
                  title="유닛 이미지"
                  setFile={setImageFile}
                  defaultImageUrl={unitData?.image?.url}
                />
              </div>
            </section>
            <div className="sticky bottom-0 z-10 flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 bg-white px-5 py-4 shadow-[0_-4px_12px_rgba(0,0,0,0.03)]">
              <p className="text-xs text-gray-500">
                {isSubmitting
                  ? '선택유닛 정보를 저장하고 있습니다.'
                  : '변경한 내용은 저장 후 적용됩니다.'}
              </p>
              <div className="ml-auto flex gap-2">
                <button
                  type="button"
                  onClick={() => navigate('/gift-seller/choice-unit')}
                  disabled={isSubmitting}
                  className="rounded border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-700 disabled:opacity-40"
                >
                  취소
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="rounded bg-indigo-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-40"
                >
                  {isSubmitting ? '저장 중...' : isEditMode ? '수정하기' : '등록하기'}
                </button>
              </div>
            </div>
          </fieldset>
        </form>
      </div>
    </div>
  );
};
